'use client';

import { useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { usePmsProviderLabel } from '@/components/shared/add-document';
import { ReplaceDocument } from './replace-document';
import { DEFAULT_REPLACE_SOURCES } from './types';
import type { ReplaceDocumentProps, UseReplaceDocumentResult } from './types';

/**
 * The one shared **Replace** affordance (VALE-1135).
 *
 * Before this seam, Replace was nine independent implementations across three
 * mechanisms (five hand-rolled hidden file inputs, three bespoke per-type
 * dialogs, and one three-item menu), and they disagreed on the only two
 * questions that matter: which sources a replacement may come from, and what
 * happens to the document being superseded. Each hidden input also carried its
 * own bug history, fixed one surface at a time: the DOM-`id` collision that made
 * Replace on the second card of a type open the first card's picker (VALE-895),
 * the "input inside the dropdown unmounts on select" bug fixed independently on
 * two firm-level cards, and the `value = ''` reset that lets the same file be
 * re-picked after a failure, present on some and absent on others. All three are
 * fixed once, here.
 *
 * **A surface declares what is being replaced; the seam decides the rest.** It
 * takes the {@link ReplaceDocumentProps.target} the document occupies and
 * derives the applicable sources from it, never from a per-call-site opinion,
 * renders exactly **one** `Replace` menu item whatever that set turns out to be,
 * and lands every source through the same matter-requirement adapter the Add
 * affordance uses. The three-item "Replace / Replace from existing document /
 * Replace from {PMS}" shape (LF-6287) is what the single item exists to
 * collapse, and is the reference for what the copy must not become as more
 * sources arrive.
 *
 * **Displacement is not decided here.** What happens to the superseded file is
 * settled backend policy: since VALE-1154 every landing path (browser upload,
 * PMS import, choose-existing, categorise, assign-identity) runs through one
 * `fileIntoCoreSlot` / `fileIntoIdentitySlot` displacement, so a replacement
 * archives exactly one incumbent and releases its references whichever source it
 * came from. The seam's job is to reach those paths, not to restate them.
 *
 * Returns two halves. See {@link UseReplaceDocumentResult} for why they cannot
 * be one. A surface whose Replace is dispatched from outside a menu drives
 * {@link ReplaceDocument} directly instead.
 *
 * @example
 * const replace = useReplaceDocument({
 *   matterId,
 *   target: { fileType: 'primary-id', contactId, label: `${name}'s ID` },
 *   replacingFileId: primaryIdFileId,
 *   onReplaced: refresh
 * });
 * // …
 * <DropdownMenuContent>{replace.menuItem}</DropdownMenuContent>
 * {replace.surfaces}
 */
export function useReplaceDocument(
  props: ReplaceDocumentProps
): UseReplaceDocumentResult {
  const {
    matterId,
    target,
    sources = DEFAULT_REPLACE_SOURCES,
    pmsProviderLabel,
    label = 'Replace',
    disabled
  } = props;
  const [open, setOpen] = useState(false);

  // Resolved here as well as inside `ReplaceDocument` so the menu item knows
  // whether there is any source to offer at all. It is the same hook, so the
  // same cached matter read, and the same single definition of the PMS gate.
  const resolvedPmsLabel = usePmsProviderLabel(
    matterId,
    sources.includes('pms') && !pmsProviderLabel
  );
  const providerLabel = pmsProviderLabel ?? resolvedPmsLabel;
  const hasAnySource =
    !!target && sources.some((source) => source !== 'pms' || !!providerLabel);

  const menuItem = hasAnySource ? (
    <DropdownMenuItem
      key="replace-document"
      disabled={disabled}
      onSelect={() => setOpen(true)}
    >
      <RefreshCw className="mr-2 h-3.5 w-3.5" />
      {label}
    </DropdownMenuItem>
  ) : null;

  return {
    menuItem,
    surfaces: <ReplaceDocument {...props} open={open} onOpenChange={setOpen} />
  };
}
